import { useState, type FormEvent } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface LandingPageProps {
  isSubmitting: boolean;
  error: string | null;
  onLogin: (username: string, password: string) => Promise<void>;
}

export function LandingPage({ isSubmitting, error, onLogin }: LandingPageProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await onLogin(username, password);
  };

  return (
    <div className="min-h-screen">
      <main className="container mx-auto grid min-h-screen max-w-4xl items-center px-6 py-12">
        <div className="grid gap-8 md:grid-cols-[1.1fr_1fr]">
          <section className="flex flex-col justify-center gap-4">
            <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Shared Service Platform</p>
            <h1 className="text-4xl font-semibold leading-tight text-foreground">One server. Shared users. Focused tools.</h1>
            <p className="max-w-md text-sm leading-6 text-muted-foreground">
              Sign in to access the services assigned to your account.
            </p>
          </section>

          <Card className="self-center">
            <CardHeader className="gap-2">
              <CardTitle>Sign in</CardTitle>
              <CardDescription>Use your existing account credentials.</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="landing-username">Username</Label>
                  <Input
                    id="landing-username"
                    value={username}
                    onChange={event => setUsername(event.currentTarget.value)}
                    autoComplete="username"
                    placeholder="bun-server"
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="landing-password">Password</Label>
                  <Input
                    id="landing-password"
                    type="password"
                    value={password}
                    onChange={event => setPassword(event.currentTarget.value)}
                    autoComplete="current-password"
                    placeholder="Enter your password"
                  />
                </div>

                {error ? <p className="text-sm text-red-600">{error}</p> : null}

                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Login"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
